import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ArrowRight, Download, ExternalLink, Globe2, Monitor, Smartphone } from 'lucide-react';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';
import {
  DESKTOP_VERSION,
  GITHUB_DESKTOP_TAG,
  GITHUB_MOBILE_TAG,
  MOBILE_VERSION,
  OS_LABEL,
  RELEASES_HOST,
  type DesktopPackage,
  type DetectedOs,
  type MacArch,
  desktopPackageUrl,
  detectMacArch,
  detectOs,
  macPackage,
  macPackages,
  mobileApkUrl,
  primaryPackageFor,
} from './shared';

type PlatformCard = {
  num: string;
  Icon: typeof Monitor;
  title: string;
  desc: string;
  status: string;
  meta: ReadonlyArray<{ k: string; v: string }>;
  featured?: boolean;
};

function WebCard() {
  const card: PlatformCard = {
    num: '01 / WEB',
    Icon: Globe2,
    title: 'Application Web',
    desc: 'Rien à installer. Ouvre velko-pos.com dans Chrome, Safari ou Firefox. Parfait pour le gérant à distance ou le comptable.',
    status: 'Disponible',
    meta: [
      { k: 'Usage idéal', v: 'Gérant · back-office' },
      { k: 'Prérequis', v: 'Navigateur moderne' },
      { k: 'Offline', v: 'Partiel (PWA)' },
    ],
  };
  return (
    <PlatformShell card={card}>
      <Link
        to="/login"
        className="inline-flex items-center justify-center gap-2 border-2 border-charcoal-800 bg-lime-500 px-5 py-3 font-marketing-display text-[14px] font-bold text-charcoal-800 shadow-neobrutal-4 transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-6"
      >
        Ouvrir dans le navigateur
        <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
      </Link>
    </PlatformShell>
  );
}

function DesktopCard() {
  const [os, setOs] = useState<DetectedOs>('other');
  const [pkg, setPkg] = useState<DesktopPackage | null>(null);
  const [macArch, setMacArch] = useState<MacArch>('unknown');

  useEffect(() => {
    const detected = detectOs();
    setOs(detected);
    setPkg(primaryPackageFor(detected));

    if (detected === 'mac') {
      detectMacArch().then((arch) => {
        setMacArch(arch);
        if (arch !== 'unknown') {
          setPkg(macPackage(arch));
        }
      });
    }
  }, []);

  const card: PlatformCard = {
    num: '02 / DESKTOP',
    Icon: Monitor,
    title: 'Application Desktop',
    desc: 'La caisse principale pour le comptoir. Raccourcis clavier, impression thermique ESC/POS, base locale chiffrée. Marche 100% hors-ligne.',
    status: 'Recommandé',
    meta: [
      { k: 'Usage idéal', v: 'Caisse comptoir' },
      { k: 'Plateformes', v: 'Win · Mac · Linux' },
      { k: 'Offline', v: '100% natif' },
    ],
    featured: true,
  };

  const showMacDualCta = os === 'mac' && macArch === 'unknown';
  const mac = showMacDualCta ? macPackages() : null;

  return (
    <PlatformShell card={card}>
      {showMacDualCta && mac ? (
        <div className="flex flex-col gap-2">
          <a
            href={desktopPackageUrl(mac.arm64)}
            className="inline-flex items-center justify-center gap-2 border-2 border-lime-500 bg-lime-500 px-5 py-3 font-marketing-display text-[13px] font-bold text-charcoal-800 shadow-[4px_4px_0_#ffffff] transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[6px_6px_0_#ffffff]"
          >
            <Download className="h-4 w-4" strokeWidth={2.5} />
            Télécharger · Apple Silicon
          </a>
          <a
            href={desktopPackageUrl(mac.intel)}
            className="inline-flex items-center justify-center gap-2 border-2 border-lime-500 bg-charcoal-700 px-5 py-3 font-marketing-display text-[13px] font-bold text-lime-500 transition-colors duration-150 hover:bg-charcoal-600"
          >
            <Download className="h-4 w-4" strokeWidth={2.5} />
            Télécharger · Mac Intel
          </a>
        </div>
      ) : pkg ? (
        <a
          href={desktopPackageUrl(pkg)}
          className="inline-flex items-center justify-center gap-2 border-2 border-lime-500 bg-lime-500 px-5 py-3 font-marketing-display text-[14px] font-bold text-charcoal-800 shadow-[4px_4px_0_#ffffff] transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[6px_6px_0_#ffffff]"
        >
          <Download className="h-4 w-4" strokeWidth={2.5} />
          Télécharger · {OS_LABEL[os] === 'Ton système' ? 'Windows' : OS_LABEL[os]}
        </a>
      ) : null}
      <a
        href={`${RELEASES_HOST}/${GITHUB_DESKTOP_TAG}/`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1.5 font-marketing-mono text-[11px] uppercase tracking-[0.08em] text-charcoal-200 hover:text-lime-400"
      >
        Voir toutes les versions
        <ExternalLink className="h-3 w-3" />
      </a>
    </PlatformShell>
  );
}

function MobileCard() {
  const card: PlatformCard = {
    num: '03 / MOBILE',
    Icon: Smartphone,
    title: 'Application Mobile',
    desc: 'Pour le livreur, le commercial sur la route, le marché hebdomadaire. Scan code-barres, impression Bluetooth, partage ticket WhatsApp.',
    status: 'Android · iOS bientôt',
    meta: [
      { k: 'Usage idéal', v: 'Terrain · livreur' },
      { k: 'Plateforme', v: 'Android 8+ · APK' },
      { k: 'iOS', v: 'En préparation' },
    ],
  };
  return (
    <PlatformShell card={card}>
      <a
        href={mobileApkUrl()}
        className="inline-flex items-center justify-center gap-2 border-2 border-charcoal-800 bg-lime-500 px-5 py-3 font-marketing-display text-[14px] font-bold text-charcoal-800 shadow-neobrutal-4 transition-transform duration-150 hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-neobrutal-6"
      >
        <Download className="h-4 w-4" strokeWidth={2.5} />
        Télécharger .apk · v{MOBILE_VERSION}
      </a>
      <a
        href={`${RELEASES_HOST}/${GITHUB_MOBILE_TAG}/`}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-flex items-center gap-1.5 font-marketing-mono text-[11px] uppercase tracking-[0.08em] text-charcoal-500 hover:text-lime-600"
      >
        Voir toutes les versions
        <ExternalLink className="h-3 w-3" />
      </a>
    </PlatformShell>
  );
}

function PlatformShell({ card, children }: { card: PlatformCard; children: React.ReactNode }) {
  const { num, Icon, title, desc, status, meta, featured } = card;
  return (
    <article
      className={`flex flex-col border-[3px] border-charcoal-800 p-8 transition-transform duration-150 hover:-translate-x-[3px] hover:-translate-y-[3px] ${
        featured
          ? 'bg-charcoal-800 text-white shadow-neobrutal-lime-10 hover:shadow-[12px_12px_0_var(--color-lime-500)]'
          : 'bg-paper shadow-neobrutal-8 hover:shadow-neobrutal-14'
      }`}
    >
      <div className="flex items-start justify-between">
        <span
          className={`font-marketing-display text-[13px] font-bold ${
            featured ? 'text-lime-400' : 'text-charcoal-400'
          }`}
        >
          {num}
        </span>
        <span className="border-[1.5px] border-charcoal-800 bg-lime-500 px-2 py-[3px] font-marketing-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-charcoal-800">
          {status}
        </span>
      </div>

      <span
        className={`mt-6 flex h-16 w-16 items-center justify-center border-2 border-charcoal-800 bg-lime-500 text-charcoal-800 ${
          featured ? 'shadow-[4px_4px_0_var(--color-lime-500)]' : 'shadow-neobrutal-4'
        }`}
      >
        <Icon className="h-[30px] w-[30px]" strokeWidth={2} />
      </span>

      <h3 className="mt-5 font-marketing-display text-[26px] font-bold leading-[1.1] tracking-[-0.02em]">
        {title}
      </h3>
      <p
        className={`mt-3 flex-1 font-marketing-body text-[14px] leading-[1.55] ${
          featured ? 'text-charcoal-200' : 'text-charcoal-600'
        }`}
      >
        {desc}
      </p>

      <dl
        className={`mt-5 flex flex-col gap-1.5 border-[1.5px] p-3 font-marketing-mono text-[11px] ${
          featured ? 'border-charcoal-700 bg-charcoal-700' : 'border-charcoal-800 bg-white'
        }`}
      >
        {meta.map(({ k, v }) => (
          <div key={k} className="flex justify-between gap-3">
            <dt
              className={`uppercase tracking-[0.06em] ${
                featured ? 'text-charcoal-400' : 'text-charcoal-500'
              }`}
            >
              {k}
            </dt>
            <dd className={`font-semibold ${featured ? 'text-lime-400' : 'text-charcoal-800'}`}>
              {v}
            </dd>
          </div>
        ))}
      </dl>

      <div className="mt-6 flex flex-col items-stretch">{children}</div>
    </article>
  );
}

export default function DownloadPlatformsV2() {
  return (
    <section
      id="download-platforms"
      className="border-y-2 border-charcoal-800 bg-white py-20 md:py-24"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <SectionLabel>3 plateformes · v{DESKTOP_VERSION}</SectionLabel>
        <h2 className="mt-3 font-marketing-display text-[clamp(32px,4.4vw,56px)] font-bold leading-[1] tracking-[-0.035em] text-charcoal-800">
          Choisis ton point <span className="underline-lime">d'entrée.</span>
        </h2>
        <p className="mt-4 max-w-xl font-marketing-body text-[17px] text-charcoal-600">
          Commence par n'importe laquelle : tout est synchronisé automatiquement. Même compte, même
          data, même heure locale.
        </p>

        <div className="mt-12 grid gap-6 md:grid-cols-3">
          <WebCard />
          <DesktopCard />
          <MobileCard />
        </div>

        <p className="mt-8 flex flex-wrap items-center gap-2 font-marketing-mono text-[11px] uppercase tracking-[0.08em] text-charcoal-500">
          Binaires open-source publiés sur GitHub Releases ·
          <Link
            to="/guide-installation"
            className="inline-flex items-center gap-1 text-charcoal-800 underline decoration-dotted underline-offset-[4px] hover:text-lime-600"
          >
            Guide d'installation
            <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
          </Link>
        </p>
      </div>
    </section>
  );
}
